import type { InvoiceStatus, InvoiceType, InvoiceLanguage } from '../../../services/apiService';
import type { StatusTone } from '../ui/StatusPill';

// ---- Status → tone + label mapping ----

interface StatusMeta {
  tone: StatusTone;
  labelDe: string;
}

const STATUS_META: Record<InvoiceStatus, StatusMeta> = {
  draft:    { tone: 'neutral', labelDe: 'Entwurf' },
  sent:     { tone: 'info',    labelDe: 'Versendet' },
  accepted: { tone: 'accent',  labelDe: 'Angenommen' },
  rejected: { tone: 'danger',  labelDe: 'Abgelehnt' },
  issued:   { tone: 'info',    labelDe: 'Ausgestellt' },
  paid:     { tone: 'success', labelDe: 'Bezahlt' },
  canceled: { tone: 'danger',  labelDe: 'Storniert' },
};

export const statusTone = (s: InvoiceStatus): StatusTone => STATUS_META[s]?.tone || 'neutral';
export const statusLabel = (s: InvoiceStatus): string => STATUS_META[s]?.labelDe || s;

// ---- Type labels ----

const TYPE_LABELS: Record<InvoiceType, string> = {
  offer: 'Angebot',
  invoice: 'Rechnung',
  cancellation: 'Stornorechnung',
};
export const typeLabel = (t: InvoiceType): string => TYPE_LABELS[t] || t;

// ---- Money / date formatters (DE locale, EUR) ----

const eurFmt = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const fmtMoney = (cents: number, currency: string = 'EUR'): string => {
  const sign = cents < 0 ? '-' : '';
  const n = Math.abs(Number(cents) || 0) / 100;
  return `${sign}${eurFmt.format(n)} ${currency}`;
};

export const fmtMoneyPlain = (cents: number): string => {
  const sign = cents < 0 ? '-' : '';
  const n = Math.abs(Number(cents) || 0) / 100;
  return `${sign}${eurFmt.format(n)}`;
};

export const parseMoneyInput = (text: string): number => {
  // Accept "1.450,00" / "1450" / "1450.00" / "1,450.00"
  if (!text) return 0;
  const cleaned = text.trim().replace(/\s+/g, '');
  // If both . and , present, the rightmost is the decimal separator.
  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  let normalized: string;
  if (lastDot === -1 && lastComma === -1) {
    normalized = cleaned;
  } else if (lastDot > lastComma) {
    // dot is decimal — strip commas
    normalized = cleaned.replace(/,/g, '');
  } else {
    // comma is decimal — strip dots, replace comma with dot
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};

export const fmtDateDe = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

// ---- Language meta ----

export const LANG_LABEL: Record<InvoiceLanguage, string> = {
  de: 'Deutsch',
  es: 'Español',
  en: 'English',
};
